/**
 * Indexability Contract — THE single source of truth for whether a URL should be
 * indexed, and how.
 *
 * Every surface that expresses an indexability signal MUST derive from
 * `pathDisposition()` so the signals can never disagree:
 *   • the sitemap            — include a slug IFF `isIndexable(slug)`
 *   • the Next.js middleware  — `X-Robots-Tag` header (`withCrawlPolicyHeader`)
 *   • page `robots` metadata  — index/noindex per route
 *   • the D1 worker renderer  — noindex meta for non-indexable served rows
 *   • internal-link `rel`     — nofollow / omit for non-index targets
 *
 * Why this module exists (2026-06-15 GSC indexability audit). Disposition used to
 * live in 4+ decoupled code paths (D1 `index_status`, `sitemap_derived_clusters`
 * additions, the `isWeakGeneratedSurface`/`isWeakIndexableValuePath` sitemap
 * filters, middleware `X-Robots-Tag`, and per-page `robots` metadata). They drifted
 * apart per-cluster, and Google saw the contradictions: 594 indexed pages earning
 * 444K impressions/28d were missing from the sitemap (sitemap filter said "weak"
 * while the page said `index,follow`), and 298 noindex pages still ranked for
 * 291K impressions/28d (the page said `noindex` while the cluster earned demand).
 * baby-names even emitted a self-contradictory `noindex, follow, index, follow`
 * header — middleware noindex colliding with the page's `index` directive.
 *
 * The fix: ONE function decides disposition; the sitemap and the robots directive
 * both read it, so "in the sitemap" and "indexable" are the same bit by
 * construction. A guard (`scripts/check-indexability-contract.mjs`) asserts no two
 * surfaces disagree. See ADR-0028 + `.claude/rules/50-indexability-contract.md`.
 */
import {
  isPrivateCrawlPath,
  isPublicNoindexPath,
  isWeakIndexableValuePath,
  legacyCrawlCanonicalPath,
} from "./crawl-policy";
import { cityTimingSlugIsIndexable, isCityTimingCategory } from "./cities-data";
import { isPromotedFestival } from "./festival-promotion";
import { ALL_LITERAL_APP_ROUTES, EARNED_INDEX_ROUTES } from "./earned-index.generated";
import { IMPRESSION_SINK_ROUTES } from "./impression-sink.generated";

export type Disposition =
  | { readonly kind: "index" }
  | { readonly kind: "noindex"; readonly follow: boolean }
  | { readonly kind: "redirect"; readonly target: string }
  | { readonly kind: "gone" };

/**
 * Normalize a slug (`"a/b"`) or a pathname (`"/a/b"` / `"/a/b/"`) to the canonical
 * pathname form (`"/a/b/"`) used by the crawl-policy predicates. A file-like leaf
 * segment (contains a `.`) keeps its form and never gains a trailing slash.
 */
function toPathname(slugOrPath: string): string {
  const trimmed = slugOrPath.replace(/^\/+|\/+$/g, "");
  if (trimmed === "") return "/";
  const last = trimmed.split("/").pop() ?? "";
  return last.includes(".") ? `/${trimmed}` : `/${trimmed}/`;
}

/**
 * A per-city timing detail page
 * (`choghadiya|panchang|rahu-kaal|shubh-muhurat/<city>`) for a city that is
 * NOT promoted for that tool. Handles BOTH locales: the Hindi
 * twin lives at `hi/<category>/<city>`, so an optional leading `hi/` locale
 * segment is stripped before reading the category. Without this strip a Hindi
 * unpromoted city would read `category="hi"` (not a timing category) and be
 * mis-classified as indexable, contradicting the page's own `index:false`
 * robots + the middleware X-Robots-Tag (the contract must agree across all
 * three surfaces — ADR-0029).
 *
 * The promotion check is category-aware: a city can be indexable for Panchang
 * while Choghadiya stays held out until that route's rendered copy passes the
 * duplicate-value gate.
 */
export function isUnpromotedCityTimingPath(slugOrPath: string): boolean {
  const trimmed = slugOrPath.replace(/^\/+|\/+$/g, "");
  const withoutLocale = trimmed.replace(/^hi\//, "");
  const [category, citySlug, ...rest] = withoutLocale.split("/");
  return (
    rest.length === 0 &&
    isCityTimingCategory(category ?? "") &&
    Boolean(citySlug) &&
    !cityTimingSlugIsIndexable(category, citySlug)
  );
}

/**
 * A `/calendar/festival/<id>/` (or `/hi/…` twin) detail page for a festival that
 * is NOT in the curated promotion set (`festival-promotion.ts`). Promotion is
 * the single "in the search index" bit — the sitemap projects promoted ids only
 * (`sitemap_festival_pages.ts`), so a non-promoted detail must render
 * noindex,follow or it sits indexable-but-unsitemapped in GSC
 * "Crawled — currently not indexed" limbo (2026-07-24 coverage audit: 19
 * impression-bearing orphans, mostly ekadashi/pradosh instances covered by the
 * vrat list hubs).
 */
export function isUnpromotedFestivalDetailPath(slugOrPath: string): boolean {
  const trimmed = slugOrPath.replace(/^\/+|\/+$/g, "");
  const withoutLocale = trimmed.replace(/^hi\//, "");
  const match = withoutLocale.match(/^calendar\/festival\/([^/]+)$/);
  return match !== null && !isPromotedFestival(match[1]);
}

/**
 * A page measured as a structural IMPRESSION SINK (rule 76): it ranks page-1 for
 * queries Google answers inside the SERP, so it earns impressions by
 * construction and clicks essentially never. Enough of them and the site's
 * share of the clicks its positions should earn collapses — which is what
 * preceded the 2026-07-21 site-level suppression (72.1% -> 51.4% over 5 weeks
 * while impressions grew 40% and clicks fell).
 *
 * This is an exact-path DEMOTION OVERLAY generated from
 * `data/governance/impression_sink_registry.yaml`. Three properties keep it
 * inside rule 66's scope discipline:
 *   • it only ever DEMOTES — it can never grant indexing to anything;
 *   • it matches exact paths, never prefixes or patterns, so it cannot capture
 *     a route class it does not understand;
 *   • families whose membership another registry owns (the per-city timing
 *     cohort → `city_index_registry.json`) are DELEGATED by the gate and never
 *     written here, so no two registries can disagree about one path.
 *
 * Demotion is `noindex,follow`: the page stays live for users, its links keep
 * flowing, and it leaves the sitemap through this same contract. Reversible per
 * path once the query earns clicks again (the Jaipur promotion pattern).
 */
export function isImpressionSinkPath(slugOrPath: string): boolean {
  return IMPRESSION_SINK_ROUTES.has(toPathname(slugOrPath));
}

/**
 * Thin / held-out generated surfaces that are crawlable but kept out of the index
 * (`noindex, follow` — Google should not index them but should still follow links
 * to the hub). This is the ONLY place "too thin to index" is decided, so the
 * sitemap exclusion and the noindex directive read the same predicate and cannot
 * drift. Today: the crawl-policy weak-value detail prefixes (currently empty after
 * the 2026-06-15 demand-led promotion), the unpromoted city-timing cohort, the
 * unpromoted festival details, and the measured impression sinks (rule 76).
 */
export function isThinNoindexPath(slugOrPath: string): boolean {
  const pathname = toPathname(slugOrPath);
  return (
    isWeakIndexableValuePath(pathname) ||
    isUnpromotedCityTimingPath(slugOrPath) ||
    isUnpromotedFestivalDetailPath(slugOrPath) ||
    isImpressionSinkPath(slugOrPath)
  );
}

/**
 * Earned-index contract (rule 66): indexing is EARNED, not default. A literal
 * app route that exists in the src/app scan but is NOT grandfathered/earned in
 * `data/governance/earned_index_registry.yaml` renders noindex,follow until it
 * passes the page-quality contract (`execution/page_quality_gate.py --earn`).
 * Scoped strictly to the scanned literal-route set, so D1/pSEO slugs and
 * dynamic cohort routes (which carry their own gates) are never affected.
 * Grandfathered pages are never demoted here — a weak page goes to the
 * enrichment queue (fix-forward), never back to noindex.
 */
export function isUnearnedLiteralRoute(slugOrPath: string): boolean {
  const pathname = toPathname(slugOrPath);
  return ALL_LITERAL_APP_ROUTES.has(pathname) && !EARNED_INDEX_ROUTES.has(pathname);
}

/**
 * The authoritative disposition for a path. Precedence (first match wins):
 *   redirect          — a legacy slug that 301s to its canonical
 *   noindex,nofollow  — private / per-user surfaces (account, cart, checkout, …)
 *   noindex,follow    — public redirectors and time-slice utility pages
 *   noindex,follow    — thin / held-out generated surfaces
 *   noindex,follow    — a NEW literal route that has not yet EARNED indexing (rule 66)
 *   index             — everything else (in sitemap, self-canonical, crawlable)
 */
export function pathDisposition(slugOrPath: string): Disposition {
  const pathname = toPathname(slugOrPath);
  const redirectTarget = legacyCrawlCanonicalPath(pathname);
  if (redirectTarget) return { kind: "redirect", target: redirectTarget };
  if (isPrivateCrawlPath(pathname)) return { kind: "noindex", follow: false };
  if (isPublicNoindexPath(pathname)) return { kind: "noindex", follow: true };
  if (isThinNoindexPath(slugOrPath)) return { kind: "noindex", follow: true };
  if (isUnearnedLiteralRoute(slugOrPath)) return { kind: "noindex", follow: true };
  return { kind: "index" };
}

/**
 * True IFF the URL belongs in the sitemap and should be crawlable + indexable.
 * The sitemap MUST include a slug iff this returns true — that equivalence is the
 * whole point of the contract (and is guard-enforced).
 */
export function isIndexable(slugOrPath: string): boolean {
  return pathDisposition(slugOrPath).kind === "index";
}

/** Next Metadata projection of the same disposition used by sitemap + headers. */
export function robotsForPath(slugOrPath: string): { index: boolean; follow: boolean } {
  const disposition = pathDisposition(slugOrPath);
  if (disposition.kind === "index") return { index: true, follow: true };
  if (disposition.kind === "noindex") {
    return { index: false, follow: disposition.follow };
  }
  return { index: false, follow: false };
}
